import { DatePipe } from "@angular/common";
import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  Input,
  OnInit,
  Output,
  SimpleChanges,
  ViewChild,
  ViewContainerRef,
} from "@angular/core";
import { MatDialog } from "@angular/material/dialog";
import { ActivatedRoute } from "@angular/router";
import { ConfirmDialogComponent, ConfirmDialogData } from "@cotecna/dialogs";
import {
  ControlType,
  Field,
  FormComponent,
  FormResult,
  FormReturnType,
  NaturalFormConfig,
  NaturalFormFlavour,
  NaturalFormStyle,
  Section,
} from "@cotecna/dynamic-view-engine";
import {
  AdditionalFieldControlType,
  Preset,
} from "src/app/settings/models/preset";

import { COMDIV, COTECNA_EYE, FALCON } from "../../constants/constants";
import { AdditionalInfoDialogComponent } from "../../dialogs/additional-info-dialog/additional-info-dialog.component";
import { GeneralInfo } from "../../model/general-info";
import { GeneralInfoService } from "../../services/general-info.service";
import {
  ADDITIONAL_FIELDS_FORM,
  ADDRESS_FIELD_ID,
  APPLICATION_ORIGIN_FIELD_ID,
  CLIENT_FIELD_ID,
  COMDIV_ORIGIN_ID,
  DATE_FIELD_ID,
  DATE_FIELD_PROPERTY_NAME,
  EXTERNAL_FIELD_ID,
  GENERAL_INFO_CONTROLS,
  GENERAL_INFO_FORM,
  INSPECTION_TYPE_FIELD_ID,
  SOW_FIELD_ID,
} from "../../static-forms/general-info-form";
import { AdditionalInfoInputComponent } from "../additional-info-input/additional-info-input.component";

@Component({
  selector: "im-general-info",
  templateUrl: "./general-info.component.html",
  styleUrls: ["./general-info.component.scss"],
  standalone: false,
})
export class GeneralInfoComponent implements OnInit, AfterViewInit {
  @ViewChild("naturalForm")
  public form: FormComponent;

  @ViewChild("container", { read: ViewContainerRef })
  public container: ViewContainerRef;

  @Input()
  public existingData: GeneralInfo;

  @Input()
  public originApplicationName: string;

  @Input()
  public clientId: string;

  @Input()
  public id: GeneralInfo;

  @Input()
  public externalId: string;

  @Input()
  public presets: Preset[];

  private client: string;
  private inspectionHasExternalSource: boolean;
  private isViewInitialized: boolean = false;

  @Output()
  public formChange: EventEmitter<FormResult> = new EventEmitter<FormResult>(
    false,
  );

  @Output()
  public presetChange: EventEmitter<Preset> = new EventEmitter<Preset>();

  @Input()
  public currentPreset: Preset;

  public naturalFormConfig: NaturalFormConfig = <NaturalFormConfig>{
    displayMode: NaturalFormStyle.material,
    flavor: NaturalFormFlavour.informative,
    notifyFieldChange: true,
    returnType: FormReturnType.entity,
  };

  public formConfigDialog: NaturalFormConfig = <NaturalFormConfig>{
    displayMode: NaturalFormStyle.flat,
    flavor: NaturalFormFlavour.informative,
    notifyFieldChange: true,
    returnType: FormReturnType.entity,
  };

  public sections: Section[];
  public controls: any[] = GENERAL_INFO_CONTROLS;

  private fieldOrder = 8; // as 7 fields are fixed
  private originalReadableValuePosition: number = -1;

  public constructor(
    public generalInfoService: GeneralInfoService,
    private dialog: MatDialog,
    private route: ActivatedRoute,
    private changeDetector: ChangeDetectorRef,
    private datePipe: DatePipe,
  ) {}

  public ngOnChanges(changes: SimpleChanges) {
    if (changes?.currentPreset != null && this.isViewInitialized) {
      this.setPresetInfo();
    }
  }

  public ngOnInit() {
    this.initializeData();
    this.inspectionHasExternalSource =
      !!this.route.snapshot.params?.id &&
      this.originApplicationName.toLocaleLowerCase() == COMDIV;
  }

  public ngAfterViewInit() {
    this.isViewInitialized = true;
    if (
      this.generalInfoService.getOthersComponents().length ||
      this.existingData?.additionalFields?.length
    ) {
      this.setGeneralInfoOtherFields();
    }
    if (this.existingData) {
      this.fillFormWithExistingData();
      this.changeDetector.detectChanges();
    }

    this.form.fieldChange.subscribe((field: Field) => {
      if (field.propertyName == CLIENT_FIELD_ID) {
        this.client = field.readableValue;
      }
      this.updatePresets(field);
      this.emitFormChange();
    });

    this.emitFormChange();
    if (this.currentPreset) {
      this.setPresetInfo();
    }
  }

  private fillFormWithExistingData() {
    Object.entries(this.existingData).forEach(([key, value]) =>
      this.addValueToForm(key, value),
    );
    this.addDatesToForm();
    this.addValueToForm(
      APPLICATION_ORIGIN_FIELD_ID,
      this.originApplicationName,
    );
    this.addValueToForm(
      CLIENT_FIELD_ID,
      this.clientId,
      this.existingData.client,
    );
    if (this.currentPreset) {
      this.addValueToForm(
        INSPECTION_TYPE_FIELD_ID,
        this.currentPreset,
        this.currentPreset.name,
      );

      if (
        this.generalInfoService.sections[0].fields.find(
          (f) => f.propertyName == APPLICATION_ORIGIN_FIELD_ID,
        ).value == COMDIV_ORIGIN_ID
      ) {
        this.presetChange.emit(this.currentPreset);
        if (this.currentPreset.scopeOfWorkDetails) {
          const sowField = this.generalInfoService.sections[0].fields.find(
            (f) => f.propertyName == SOW_FIELD_ID,
          );
          const field = this.sections[0].fields.find(
            (f) => f.id == sowField.id,
          );
          if (field.value == null) {
            field.value = this.currentPreset.scopeOfWorkDetails;
          }
        }
        if (
          this.existingData.originApplicationName?.toUpperCase() ===
            COTECNA_EYE &&
          (this.currentPreset.client || this.currentPreset.client == "")
        ) {
          const clientField = this.generalInfoService.sections[0].fields.find(
            (f) => f.propertyName == CLIENT_FIELD_ID,
          );
          this.sections[0].fields.find((f) => f.id == clientField.id).value =
            this.currentPreset.clientId;
          this.sections[0].fields.find(
            (f) => f.id == clientField.id,
          ).readableValue = this.currentPreset.client;
          this.client = this.currentPreset.client;
        }
      }

      if (
        this.generalInfoService.sections[0].fields
          .find((f) => f.propertyName == APPLICATION_ORIGIN_FIELD_ID)
          .value.toLocaleLowerCase() == FALCON
      ) {
        this.sections[0].fields.find(
          (f) => f.propertyName === EXTERNAL_FIELD_ID,
        ).readOnly = true;
      }
    }
  }

  private addDatesToForm() {
    const dates = this.existingData.date as Date[];
    if (dates?.length) {
      const formattedDates = dates.map((date) =>
        this.datePipe.transform(new Date(date), "yyyy-MM-ddTHH:mm"),
      );
      this.addValueToForm(DATE_FIELD_PROPERTY_NAME, formattedDates.join(","));
    }
  }

  private addValueToForm(
    propertyName: string,
    value: any,
    readableValue = null,
  ) {
    const field = this.form.sections[0].fields.find(
      (f) => f.propertyName == propertyName,
    );
    let fieldExistsAndHasValue = false;
    if (propertyName === ADDRESS_FIELD_ID)
      fieldExistsAndHasValue = !!(field && value.text);
    else fieldExistsAndHasValue = !!(field && value);

    if (fieldExistsAndHasValue) {
      field.readOnly = this.inspectionHasExternalSource;
      field.valid = true;
      field.value = value;
      field.readableValue = readableValue;
    }
    if (propertyName == CLIENT_FIELD_ID) {
      this.client = readableValue;
    }
  }

  public addField(): void {
    const dialogRef = this.showDialog(
      "Add Additional Field",
      "ADD",
      null,
      null,
      false,
    );

    dialogRef.afterClosed().subscribe((result) => {
      if (result?.data) {
        this.addGeneralInfoOtherField(
          result.data.fieldname,
          result.data.value,
          result.data.mandatory,
        );
        this.emitFormChange();
      }
    });
  }

  private initializeData() {
    this.setPresets();

    if (this.generalInfoService.sections) {
      this.sections = this.generalInfoService.sections;
      // other fields are added only in the AfterViewInit as the ViewChildContainer is needed
    } else {
      this.sections = JSON.parse(JSON.stringify(GENERAL_INFO_FORM));
      this.generalInfoService.sections = this.sections;
    }
  }

  private async setPresets() {
    if (this.presets && this.presets.length > 0) {
      this.controls.find((f) => f.id === INSPECTION_TYPE_FIELD_ID).options =
        this.presets.map((p) => {
          return { id: p.id, name: p.name };
        });
    }
  }

  private setGeneralInfoOtherFields(): void {
    const otherFields: {
      id: string;
      name: string;
      value: string;
      selectionOptions: string[];
      controlType: AdditionalFieldControlType;
      mandatory: boolean;
    }[] = [];
    if (this.generalInfoService?.getOthersComponents()?.length)
      this.generalInfoService.getOthersComponents().forEach((component) => {
        otherFields.push({
          id: component.instance.field.id,
          name: component.instance.field.label,
          value: component.instance.field.value,
          selectionOptions: component.instance.field.selectionOptions,
          controlType: component.instance.field.controlType,
          mandatory: component.instance.mandatory,
        });
      });
    else if (this.existingData?.additionalFields?.length) {
      this.existingData.additionalFields.forEach((o) => {
        otherFields.push({
          id: o.id,
          name: o.name,
          value: o.value,
          selectionOptions: o.selectionOptions,
          controlType: o.controlType,
          mandatory: o.mandatory,
        });
      });
    }

    this.generalInfoService.removeAllOthersComponents();

    otherFields.forEach((field) => {
      this.addAdditionalFieldComponent(
        field.id,
        field.name,
        field.value,
        field.controlType,
        field.mandatory,
      );
    });
  }

  private addAdditionalFieldComponent(
    id: string,
    name: string,
    value: string,
    controlType: AdditionalFieldControlType = AdditionalFieldControlType.Input,
    mandatory: boolean,
  ) {
    const fieldToAdd: Field = id
      ? this.generateNewField(name, value, mandatory, id)
      : this.generateNewField(name, value, mandatory);
    this.addComponent(fieldToAdd, controlType, mandatory);
  }

  private addGeneralInfoOtherField(
    label: string,
    value: string,
    mandatory: boolean,
    originName: string = "",
    controlType: AdditionalFieldControlType = AdditionalFieldControlType.Input,
  ) {
    const other = originName
      ? this.existingData.availablePrefilledOthers.find(
          (o) => o.key == originName,
        )
      : this.existingData.availablePrefilledOthers.find((o) => o.key == label);
    if (other) value = other.value;
    if (!this.existingData.additionalFields.find((o) => o.name == label)) {
      this.addAdditionalFieldComponent(
        null,
        label,
        value ?? "",
        controlType,
        mandatory,
      );
    }
  }

  private generateNewField(
    name: string,
    value: string,
    mandatory: boolean,
    id?: string,
  ): Field {
    return id
      ? this.generalInfoService.generateNewField(
          name,
          value,
          this.fieldOrder++,
          mandatory,
          id,
        )
      : this.generalInfoService.generateNewField(
          name,
          value,
          this.fieldOrder++,
          mandatory,
        );
  }

  private updatePresets(updatedField: Field) {
    if (
      updatedField.id == INSPECTION_TYPE_FIELD_ID &&
      (!this.currentPreset || updatedField.value != this.currentPreset)
    ) {
      const previousPreset = this.currentPreset ? this.currentPreset : null;
      this.currentPreset = this.presets.find((p) => p.id == updatedField.value);
      this.presetChange.emit(this.currentPreset);

      this.removePreviousPreset(previousPreset);
      this.setPresetInfo();
    }
  }

  private setPresetInfo() {
    if (this.currentPreset.scopeOfWorkDetails) {
      const sowField = this.generalInfoService.sections[0].fields.find(
        (f) => f.propertyName == SOW_FIELD_ID,
      );
      this.sections[0].fields.find((f) => f.id == sowField.id).value =
        this.currentPreset.scopeOfWorkDetails;
    }
    if (
      this.existingData.originApplicationName?.toUpperCase() === COTECNA_EYE &&
      !this.existingData.client &&
      (this.currentPreset.client || this.currentPreset.client == "")
    ) {
      const clientField = this.generalInfoService.sections[0].fields.find(
        (f) => f.propertyName == CLIENT_FIELD_ID,
      );
      this.sections[0].fields.find((f) => f.id == clientField.id).value =
        this.currentPreset.clientId;
      this.sections[0].fields.find(
        (f) => f.id == clientField.id,
      ).readableValue = this.currentPreset.client;
      this.client = this.currentPreset.client;
    }
    this.currentPreset.additionalFields.forEach((additionalField) => {
      this.addGeneralInfoOtherField(
        additionalField.name,
        additionalField.value,
        additionalField.mandatory,
        additionalField.originName,
        additionalField.controlType,
      );
    });
  }

  private removePreviousPreset(preset: Preset) {
    if (preset) {
      preset.additionalFields.forEach((additionalField) => {
        const otherToRemove = this.existingData.additionalFields.findIndex(
          (o) => o.name == additionalField.name,
        );
        this.existingData.additionalFields.splice(otherToRemove, 1);
        const positionToRemove =
          this.generalInfoService.getOthersComponentIndexByLabel(
            additionalField.name,
          );
        this.removeOthersFieldsFromDOM(positionToRemove);
      });
    }
  }

  private emitFormChange(): void {
    const data = { ...this.form.getModel() };
    data.additionalFields = [];

    this.handleInspectionDate(data);

    this.generalInfoService.getOthersComponents().forEach((component) => {
      data.additionalFields.push({
        id: component.instance.field.id,
        name: component.instance.field.label,
        value: component.instance.field.value,
        selectionOptions:
          component.instance.controlType == AdditionalFieldControlType.Selection
            ? component.instance.field.readableValue
            : [],
        controlType: component.instance.controlType,
        mandatory: component.instance.mandatory,
      });
    });

    if (!this.currentPreset || !this.currentPreset.additionalFields) {
      data.additionalFieldsComplete = true;
    } else {
      data.additionalFieldsComplete = this.currentPreset.additionalFields.every(
        (additionalField) => {
          if (additionalField.mandatory) {
            const foundItem = data.additionalFields.find(
              (item) => item.name === additionalField.name,
            );
            return foundItem?.value ? true : false;
          }
          return true;
        },
      );
    }

    if (this.client) data.client = this.client;

    //TODO - Redo address field in natural form to avoid hardcoded line
    if (data?.client)
      this.form.sections[0].fields.find((f) => f.id == CLIENT_FIELD_ID).valid =
        true;
    if (data?.address?.text.length > 0)
      this.form.sections[0].fields.find((f) => f.id == ADDRESS_FIELD_ID).valid =
        true;

    const result: FormResult = {
      isValid: this.form.isValid(),
      data: data,
    };

    data.availablePrefilledOthers = this.existingData?.availablePrefilledOthers;

    this.formChange.emit(result);
  }

  private handleInspectionDate(data: any): void {
    data[DATE_FIELD_ID] = data[DATE_FIELD_PROPERTY_NAME]?.length
      ? data[DATE_FIELD_PROPERTY_NAME]?.split(",")
      : null;
    delete data[DATE_FIELD_PROPERTY_NAME];
  }

  private showDialog(
    title: string,
    actionButtonText: string,
    label: string,
    value: string,
    mandatory: boolean,
    type?: AdditionalFieldControlType,
  ) {
    return this.dialog.open(AdditionalInfoDialogComponent, {
      data: {
        title: title,
        actionButtonText: actionButtonText,
        fieldName: label ?? "",
        value: value ?? "",
        additionalFields: this.existingData.additionalFields,
        availablePrefilledOthers: this.existingData.availablePrefilledOthers,
        mandatory: mandatory,
        type:
          type == AdditionalFieldControlType.Selection
            ? ControlType.Dropdown
            : ControlType.Input,
      },
    });
  }

  private addComponent(
    field: Field,
    controlType: AdditionalFieldControlType,
    mandatory: boolean,
  ) {
    const component = this.container.createComponent(
      AdditionalInfoInputComponent,
    );

    if (this.currentPreset) {
      const presetDetails = this.currentPreset.additionalFields.filter(
        (pr) => pr.name == field.propertyName,
      );
      if (presetDetails[0]?.selectionOptions.length > 1)
        controlType = AdditionalFieldControlType.Selection;
      if (controlType == AdditionalFieldControlType.Selection) {
        field.readableValue = presetDetails[0].selectionOptions;
        if (!field.readableValue.includes(field.value) && field.value != "") {
          field.readableValue.push(field.value);
        }
      }

      if (presetDetails[0]?.controlType == AdditionalFieldControlType.Hyperlink)
        controlType = AdditionalFieldControlType.Hyperlink;
    }

    (<AdditionalInfoInputComponent>component.instance).field = field as Field;
    (<AdditionalInfoInputComponent>component.instance).controlType =
      controlType as AdditionalFieldControlType;
    (<AdditionalInfoInputComponent>component.instance).mandatory =
      mandatory as boolean;
    (<AdditionalInfoInputComponent>component.instance).remove.subscribe(
      (field: Field) => this.removeOthersField(field),
    );
    (<AdditionalInfoInputComponent>component.instance).edit.subscribe(
      (field: any) => {
        this.editOthersField(field, field.controlType, mandatory);
      },
    );
    (<AdditionalInfoInputComponent>component.instance).update.subscribe(
      (field: Field, controlType: AdditionalFieldControlType) => {
        this.editOthersFieldCallback(field, controlType);
      },
    );

    this.generalInfoService.addOthersFieldComponent(component);
  }

  private editOthersField(
    value: any,
    controlType: AdditionalFieldControlType,
    mandatory: boolean,
  ): void {
    const sections = this.getOtherFieldsDialogForm();
    sections[0].fields.filter((f) => f.id == "fieldname")[0].value =
      value.field.label;
    sections[0].fields.filter((f) => f.id == "value")[0].value =
      value.field.value;

    const dialogRef = this.showDialog(
      "Edit Additional Field",
      "EDIT",
      value.field.label,
      value.field.value,
      mandatory,
      controlType,
    );

    dialogRef.afterClosed().subscribe((result) => {
      if (result.data) {
        value.field.label = result.data.fieldname;
        value.field.value = result.data.value;
        this.editOthersFieldCallback(value.field, value.controlType);
      }
    });
  }

  private editOthersFieldCallback(
    field: Field,
    controlType: AdditionalFieldControlType,
  ) {
    const positionToEdit = this.generalInfoService.getOthersComponentIndex(
      field.id,
    );
    if (positionToEdit > -1) {
      this.generalInfoService.setOthersComponentLabel(
        positionToEdit,
        field.label,
      );
      this.generalInfoService.setOthersComponentValue(
        positionToEdit,
        field.value,
      );
      this.generalInfoService.setOthersComponentControlType(
        positionToEdit,
        controlType,
      );
      if (controlType == AdditionalFieldControlType.Selection)
        this.generalInfoService.setOthersComponentReadableValue(
          positionToEdit,
          field.readableValue,
        );

      if (
        Array.isArray(field.readableValue) &&
        field.readableValue.length > 1 &&
        !field.readableValue.includes(field.value)
      )
        this.generalInfoService.addOthersComponentReadableValue(
          positionToEdit,
          field.value,
        );
    }
    this.emitFormChange();
  }

  private removeOthersField(field: Field) {
    const fieldLabel = this.generalInfoService
      .getOthersComponents()
      .find((component) => component.instance.field.id == field.id).instance
      .field.label;

    const dialogRef = this.dialog.open(ConfirmDialogComponent, {
      data: <ConfirmDialogData>{
        icon: "clear",
        okButton: {
          text: "delete",
          color: "warn",
        },
        title: " Delete Additional Field",
        message: `Are you sure you want to delete the ${fieldLabel} field?`,
      },
    });

    dialogRef.afterClosed().subscribe((result) => {
      if (result == true) {
        this.removeOthersFieldCallback(field.id);
      }
    });
  }

  private removeOthersFieldCallback(fieldId: string) {
    const positionToRemove =
      this.generalInfoService.getOthersComponentIndex(fieldId);
    this.removeOthersFieldsFromDOM(positionToRemove);
    this.emitFormChange();
  }

  private removeOthersFieldsFromDOM(otherFieldPosition: number) {
    if (otherFieldPosition > -1) {
      this.container.remove(otherFieldPosition);
      this.generalInfoService.spliceOthersComponent(otherFieldPosition);
    }
  }

  private getOtherFieldsDialogForm(): Section[] {
    return JSON.parse(JSON.stringify(ADDITIONAL_FIELDS_FORM));
  }

  public ValidateKeyCode(event: any) {
    this.generalInfoService.ValidateKey(event);
  }
}
